import Alert from '@risk-smart/themed-cloudscape-components/alert';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Table from '@risk-smart/themed-cloudscape-components/table';
import { Accordion } from '@risksmart-app/atomic-ui';
import Button from '@risksmart-app/components/src/button';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import TabHeader from 'src/components/tab-header';

import { useGetRiskScoreHistory } from '@/hooks/queries/risk/useGetRiskScoreHistory';
import { getCounter } from '@/utils/collectionUtils';

import { useGetRiskScoreHistoryTableProps } from './config';
import RiskScoreTrendChart from './RiskScoreTrendChart';

type Props = {
  riskId: string;
};

/**
 * Risk score history (RSP-6566) — the residual/inherent score trend chart plus
 * the audit-history table, rendered in the same `Accordion` the risk ratings
 * register uses, below the ratings. Feature-flag gating (`risk_score_history`)
 * is owned by the parent (RiskAssessmentTab), so this component just renders
 * the content.
 */
const ScoreHistorySection: FC<Props> = ({ riskId }) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'scoreHistory' });

  const { data, loading, error } = useGetRiskScoreHistory({ riskId });
  const rows = data ?? [];

  const tableProps = useGetRiskScoreHistoryTableProps(rows);

  const renderBody = () => {
    // The hook surfaces the failure via a toast (useErrorNotification); show an
    // error alert rather than the misleading "no history" empty state, since a
    // failed query also yields `data === undefined` → `rows.length === 0`.
    if (!loading && error) {
      return <Alert type={'error'}>{st('error')}</Alert>;
    }

    if (!loading && rows.length === 0) {
      return <Alert type={'info'}>{st('empty')}</Alert>;
    }

    return (
      <SpaceBetween direction={'vertical'} size={'m'}>
        <RiskScoreTrendChart rows={rows} loading={loading} />
        <Table
          {...tableProps}
          header={
            <TabHeader
              actions={
                <Button iconName={'download'} onClick={tableProps.exportToCsv}>
                  {t('export.export')}
                </Button>
              }
            >
              {st('table.title')}
            </TabHeader>
          }
          variant={'embedded'}
          loading={loading}
          trackBy={'id'}
        />
      </SpaceBetween>
    );
  };

  return (
    <Accordion defaultValue={['score-history']}>
      <Accordion.Item value={'score-history'}>
        <Accordion.Header>
          <Accordion.Trigger>
            <div className={'flex space-x-2'}>
              <span>{st('section_title')}</span>
              <span className={'text-grey font-normal'}>
                {getCounter(rows.length, loading)}
              </span>
            </div>
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content>{renderBody()}</Accordion.Content>
      </Accordion.Item>
    </Accordion>
  );
};

export default ScoreHistorySection;
